import Constants from './nqs/Services';
import Util from './utils/Util';
import Core from './Core';
import { CoreEvents, CoreTag } from '../common/Constants';
import Log from '../common/log';
import HttpClient from './net/HttpClient';
export default class FastDataService {
    /**
     * This class manages Fastdata service and view index.
     *
     * @constructs
     *
     * @param {CoreOptions} options Options from the Core
     * @param {CoreStorage} coreStorage Core storage object
     */
    constructor(options, accountCode, coreStorage, browserStorage, npawPluginOptions) {
        var _a;
        this.response = {};
        this._session = '';
        this.activeXHRs = new Set();
        /**
         * Session expire time in seconds
         */
        this.sessionExpire = 120;
        /**
         * View timeout in seconds
         */
        this.viewTimeout = 120;
        this.retries = 0;
        this.MAX_RETRIES = 25;
        this.isUsingLegacy = false;
        this.options = options;
        this.npawPluginOptions = npawPluginOptions;
        this.accountCode = accountCode;
        this.coreStorage = coreStorage;
        this.browserStorage = browserStorage;
        this._fastDataHost = this.options.getFastDataHost();
        const usingStandalone = process.env.IS_STANDALONE === 'true';
        this.isUsingLegacy = usingStandalone && typeof ((_a = npawPluginOptions === null || npawPluginOptions === void 0 ? void 0 : npawPluginOptions.legacyPlugin) === null || _a === void 0 ? void 0 : _a.viewTransform) !== 'undefined';
        /** Create interval to get configuration from LMA periodically */
        this._createConfigurationInterval();
    }
    /**
     * Starts the 'FastData' fetching. This will send the initial request to NPAW in order to get
     * the needed info for the rest of the requests.
     *
     * This is an asynchronous process.
     *
     * When the fetch is complete, {@link #fastDataConfig} will contain the parsed info.
     * @see FastDataConfig
     */
    init() {
        var _a, _b;
        // offline
        if (this.options && this.options.offline) {
            // set the options
            this.response.host = 'OFFLINE';
            this.response.code = 'OFFLINE';
            this.response.pingTime = 60;
            this.response.beatTime = 60;
            this._session = Util.generateRandomString(15);
            return;
        }
        // Do not perform /configuration and /data requests if using the legacy plugin
        if (this.isUsingLegacy)
            return;
        // reusing old data not expired
        if (this.browserStorage && this.browserStorage.isEnabled()) {
            const now = new Date().getTime();
            const sessionExpire = ((_a = Number(this.browserStorage.getLocal('sessionExpire'))) !== null && _a !== void 0 ? _a : this.sessionExpire) * 1000;
            if (now < sessionExpire + (Number(this.coreStorage.getDataTime()) || 0) && this.coreStorage.getStoredData()) {
                this.setData((_b = this.coreStorage.getStoredData()) !== null && _b !== void 0 ? _b : '');
                return;
            }
        }
        // Reset ExpirationManager
        this.coreStorage.removeLocal('firstRequestTime');
        this.coreStorage.removeLocal('lastRequestTime');
        // request new data
        const serviceData = Constants.Service.DATA;
        const dataParams = {
            system: this.accountCode,
            pluginVersion: Core.getPluginVersion(),
            requestNumber: this.getRequestNumber()
        };
        // load configuration from backend
        this.loadConfiguration();
        try {
            Log.notice(CoreTag, serviceData + ' ' + dataParams.system);
            if (dataParams.system === 'nicetest') {
                // "nicetest" is the default accountCode.
                // If found here, it's very likely that the customer has forgotten to set it.
                Log.error(CoreTag, "No accountCode has been set. Please set your accountCode inside plugin's options.");
            }
            this.performRequestToFastData(serviceData, dataParams, this._receiveData.bind(this), this._failedData.bind(this));
        }
        catch (err) {
            Log.error(CoreTag, 'Error doing get data request to FastData service.');
        }
    }
    updateOptions(options) {
        const previousEnabled = this.options.isRefreshLMAConfigurationEnabled();
        const previousSeconds = this.options.refreshLMAConfigurationSeconds();
        this.options.updateOptions(options);
        if (this.options.offline) {
            this.reset();
            this.init();
        }
        if (!this.isUsingLegacy) {
            const newEnabled = this.options.isRefreshLMAConfigurationEnabled();
            const newSeconds = this.options.refreshLMAConfigurationSeconds();
            if (previousEnabled !== newEnabled || (newEnabled && previousSeconds !== newSeconds)) {
                this._recreateConfigurationInterval();
            }
        }
    }
    /**
     * @returns Session expire time in seconds
     */
    getSessionExpireTime() {
        return this.sessionExpire;
    }
    refreshSessionToken() {
        this._session = '';
        this.browserStorage.removeStorages('data');
        this.init();
    }
    /**
     * Load configuration from backend
     */
    loadConfiguration() {
        const serviceConfiguration = Constants.Service.CONFIGURATION;
        const configurationParams = {
            system: this.accountCode,
            pluginVersion: Core.getPluginVersion(),
            requestNumber: this.getRequestNumber()
        };
        this.performRequestToFastData(serviceConfiguration, configurationParams, this._receiveConfiguration.bind(this), this._failedConfiguration.bind(this));
    }
    /**
     * Returns the current session
     *
     * @returns Session
     */
    getSession() {
        var _a, _b;
        return this.isUsingLegacy ? (_b = (_a = this.npawPluginOptions) === null || _a === void 0 ? void 0 : _a.legacyPlugin) === null || _b === void 0 ? void 0 : _b.viewTransform._session : this._session;
    }
    /**
     * Sets the session
     *
     * @param sessionId Sets the session
     */
    setSession(session) {
        this._session = session;
    }
    /** Sets the host url
     *
     * @param host Sets the host
     */
    setHost(host) {
        this.response.host = host;
    }
    getPingTime() {
        var _a;
        return (_a = this.pingTime) !== null && _a !== void 0 ? _a : 5;
    }
    getBeatTime() {
        var _a;
        return (_a = this.beatTime) !== null && _a !== void 0 ? _a : 30;
    }
    /**
     * Returns view timeout in seconds from fast data (vt parameter)
     * @returns View timeout in seconds, defaults to 120
     */
    getViewTimeoutTime() {
        var _a;
        return (_a = this.viewTimeout) !== null && _a !== void 0 ? _a : 120;
    }
    /**
     * Returns balancer enabled flag from fast data (cb parameter)
     * @returns true if cb=1, false if cb=0, undefined if not set by backend
     */
    isBalancerEnabledByFastData() {
        return this.response.balancerEnabled;
    }
    /**
     * Uses given response to set fastdata response.
     *
     * @param {String} response Fastdata response as json string.
     */
    setData(response) {
        try {
            const previousPingTime = this.pingTime;
            const resp = JSON.parse(response);
            if (this.options['parse.fdsResponseHost']) {
                // Modify the object directly instead of string manipulation
                // This avoids regex issues with special characters in host names
                const newHost = this.options['parse.fdsResponseHost'](resp.q.h);
                resp.q.h = newHost;
                response = JSON.stringify(resp);
            }
            this.response = {
                msg: response,
                host: resp.q.h,
                code: resp.q.c,
                pingTime: resp.q.pt || 5,
                beatTime: resp.q.i ? resp.q.i.bt || 30 : 30,
                sessionExpire: resp.q.st ? resp.q.st || 120 : 120,
                viewTimeout: resp.q.vt ? resp.q.vt || 120 : 120,
                balancerEnabled: typeof resp.q.cb !== 'undefined' ? resp.q.cb === 1 : undefined
            };
            this._session = this.response.code;
            this.browserStorage.setLocal('sessionExpire', this.response.sessionExpire);
            this.browserStorage.setLocal('viewExpire', this.response.viewTimeout);
            this.browserStorage.setStorages('accCode', this.accountCode);
            this.browserStorage.setStorages('session', this.response.code);
            this.coreStorage.setStoredData(this.response.msg);
            this.coreStorage.setDataTime(new Date().getTime().toString());
            this.pingTime = this.response.pingTime;
            this.beatTime = this.response.beatTime;
            this.sessionExpire = this.response.sessionExpire;
            this.viewTimeout = this.response.viewTimeout;
            if (typeof this.pingTime === 'number' && this.pingTime > 0 && this.pingTime !== previousPingTime) {
                Core.getInstance().throwEvent(CoreEvents.FAST_DATA_READY);
            }
        }
        catch (err) {
            Log.error(CoreTag, 'Fastdata response is invalid.');
        }
    }
    /**
     *
     * @param response Fastdata response as json string
     */
    setConfiguration(response) {
        try {
            this.response.configurationOptions = JSON.parse(response);
        }
        catch (err) {
            Log.error(CoreTag, 'Configuration response is invalid.');
        }
    }
    getFastDataResponse() {
        return this.response;
    }
    getAccountCode() {
        return this.accountCode;
    }
    getRequestNumber() {
        return Math.random();
    }
    /**
     * Resets the data stored on the browser
     * @private
     */
    reset() {
        this.coreStorage.removeStoredData();
        this.coreStorage.removeSession();
        this.browserStorage.removeLocal('sessionExpire');
        this.browserStorage.removeLocal('viewExpire');
        this.browserStorage.removeStorages('accCode');
        this.browserStorage.removeStorages('session');
    }
    performRequestToFastData(service, params, onSuccess, onFail) {
        if (this.options.offline)
            return;
        params['timemark'] = new Date().getTime();
        params['outputFormat'] = 'json';
        // Build URL
        const url = `https://${this._fastDataHost}${service}${Util.toQueryString(params)}`;
        // Use HttpClient to perform the request and keep XHR tracking/abort semantics
        const handle = HttpClient.request({
            url,
            method: 'GET',
            timeoutMs: 15000
        });
        // Track active XHR
        this.activeXHRs.add(handle.xhr);
        handle.promise
            .then((_resp) => {
            // Remove from active
            this.activeXHRs.delete(handle.xhr);
            if (this.options.offline)
                return;
            if (handle.xhr.status >= 200 && handle.xhr.status < 400) {
                // Call original success callback
                onSuccess(handle.xhr);
            }
            else {
                onFail(handle.xhr);
            }
        })
            .catch((_err) => {
            // Remove from active
            this.activeXHRs.delete(handle.xhr);
            onFail(handle.xhr);
        });
    }
    /**
     * Create Configuration Interval
     * @private
     */
    _createConfigurationInterval() {
        // Register only if refresh lma configuration enabled, and is not created previously
        if (!this.isUsingLegacy && this.options.isRefreshLMAConfigurationEnabled() && !this._refreshInterval) {
            this._refreshInterval = setInterval(this.loadConfiguration.bind(this), this.options.refreshLMAConfigurationSeconds() * 1000);
        }
    }
    _recreateConfigurationInterval() {
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
            this._refreshInterval = undefined;
        }
        this._createConfigurationInterval();
    }
    /**
     * Parse the response from the fastData service.
     *
     */
    _receiveData(req) {
        this.retries = 0;
        this.setData(req.response);
    }
    _receiveConfiguration(req) {
        this.setConfiguration(req.response);
    }
    _failedData(req) {
        Log.error(CoreTag, `Fastdata request has failed (attempt ${this.retries + 1}/${this.MAX_RETRIES}).`);
        if (this._retryTimeout) {
            clearTimeout(this._retryTimeout);
            this._retryTimeout = undefined;
        }
        if (++this.retries >= this.MAX_RETRIES) {
            Log.error(CoreTag, `Fastdata request failed after ${this.MAX_RETRIES} attempts. Giving up.`);
            return;
        }
        if (this.retries < 5) {
            this._retryTimeout = setTimeout(this._retryDataRequest.bind(this), 5000);
        }
        else {
            this._retryTimeout = setTimeout(this._retryDataRequest.bind(this), 60000);
        }
    }
    /**
     * Retry only the /data request without re-fetching configuration
     * @private
     */
    _retryDataRequest() {
        const serviceData = Constants.Service.DATA;
        const dataParams = {
            system: this.accountCode,
            pluginVersion: Core.getPluginVersion(),
            requestNumber: this.getRequestNumber()
        };
        try {
            Log.notice(CoreTag, `Retrying ${serviceData} request (attempt ${this.retries + 1})`);
            this.performRequestToFastData(serviceData, dataParams, this._receiveData.bind(this), this._failedData.bind(this));
        }
        catch (e) {
            Log.error(CoreTag, e);
        }
    }
    _failedConfiguration(req) {
        Log.error(CoreTag, 'Configuration request has failed.');
    }
    destroy() {
        // Clear retry timeout
        if (this._retryTimeout) {
            clearTimeout(this._retryTimeout);
            this._retryTimeout = undefined;
        }
        // Abort all active XHR requests
        const xhrsToAbort = Array.from(this.activeXHRs);
        this.activeXHRs.clear();
        for (const xhr of xhrsToAbort) {
            xhr.onload = null;
            xhr.onerror = null;
            xhr.abort();
        }
        // Clear configuration refresh interval
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
            this._refreshInterval = undefined;
        }
    }
}
